/**
 * Pure reducer for accumulating streaming SSE events into a `ConversationMessage`.
 *
 * Every event (text delta, thinking delta, function call, etc.) is folded into
 * the message immutably: each call returns a fresh `ConversationMessage` with a
 * fresh `contents` array. Consecutive `text` (and `thinking`) deltas merge into
 * a single content item rather than appending new entries.
 *
 * This module deliberately contains no React state, no side-effects, and no
 * dependencies on the consumer. It can be tested as a plain function and reused
 * by any stream consumer.
 *
 * Note: `metadata` is the *only* event that creates a new message; it is handled
 * by the consumer because it produces a `ConversationMessage` without an
 * existing one to fold into.
 */

import {
   ConversationMessage,
   MessageContent,
   MessageContentText,
   MessageContentThinking,
   StreamingConversationMessage,
} from "./types";

/**
 * Append a delta whose content should *merge* with the previous content item
 * when the previous item shares the same discriminator. Used by `text` and
 * `thinking` deltas, both of which have the same `{type, content: string}`
 * shape.
 */
const appendMergeableDelta = (
   message: ConversationMessage,
   delta: MessageContentText | MessageContentThinking,
): ConversationMessage => {
   const last = message.contents.at(-1);
   const next: MessageContent[] =
      last?.type === delta.type
         ? [
              ...message.contents.slice(0, -1),
              {...last, content: last.content + delta.content},
           ]
         : [...message.contents, delta];
   return {...message, contents: next};
};

/**
 * Append a text delta to a message, merging with the previous content item if
 * it was also text.
 */
export const appendTextDelta = (
   message: ConversationMessage,
   delta: MessageContentText,
): ConversationMessage => appendMergeableDelta(message, delta);

/**
 * Append a thinking delta to a message, merging with the previous content item
 * if it was also thinking.
 */
export const appendThinkingDelta = (
   message: ConversationMessage,
   delta: MessageContentThinking,
): ConversationMessage => appendMergeableDelta(message, delta);

/**
 * Append an arbitrary content item (function_call, function_result, citation,
 * etc.) to a message without merging.
 */
export const appendContent = (
   message: ConversationMessage,
   content: MessageContent,
): ConversationMessage => ({
   ...message,
   contents: [...message.contents, content],
});

/** Type guard: does this event create a new conversation message? */
export const isMetadataEvent = (
   event: StreamingConversationMessage,
): event is Extract<StreamingConversationMessage, {type: "metadata"}> =>
   event.type === "metadata";

/** Events that are never persisted in conversation history. */
export const isTransientEvent = (
   event: StreamingConversationMessage,
): event is Extract<
   StreamingConversationMessage,
   {type: "notification" | "token_usage" | "generation_usage" | "recalled_memories"}
> =>
   event.type === "notification" ||
   event.type === "token_usage" ||
   event.type === "generation_usage" ||
   event.type === "recalled_memories";

/**
 * Fold one streaming event into a message. Filter metadata/transient events
 * first; this reducer ignores any that reach it. Consumers decide whether
 * `status` is UI-only or persisted.
 */
export const accumulateStreamEvent = (
   message: ConversationMessage,
   event: StreamingConversationMessage,
): ConversationMessage => {
   if (event.type === "text") return appendTextDelta(message, event);
   if (event.type === "thinking") return appendThinkingDelta(message, event);
   if (event.type === "message_snapshot") {
      return {...message, contents: event.contents};
   }
   // A terminal marker: the turn could not be completed. Replace the accumulated contents rather
   // than appending, so any stray fragment leaked live (e.g. a bare `}`) is dropped from the view.
   if (event.type === "generation_failed") return { ...message, contents: [event] };
   if (isMetadataEvent(event) || isTransientEvent(event)) return message;
   if (event.type === "function_call") {
      const existingIndex = message.contents.findIndex(
         (content) =>
            content.type === "function_call" && content.callId === event.callId
      );
      if (existingIndex >= 0) {
         const contents = [...message.contents];
         contents[existingIndex] = event;
         return {...message, contents};
      }
   }
   // Catch-all: function_call, function_result, citation, generated_file,
   // document_upload, binary, bar_chart, pie_chart, script_draft, form,
   // form_submission, etc.
   return appendContent(message, event);
};
